import React, { useEffect, useRef } from 'react';
import { Box, Avatar, Typography, Link as MuiLink } from '@mui/material';
import { FRONTEND_BASE_URL } from 'utils/apiUtils';
import avatarPlaceholder from 'images/avatar-cropped.png';
import { initializeQRCode } from 'utils/emailSignature/copyEmailSignature';

const SignatureContent = React.forwardRef(({ emailSignatureState, cardId, setLoading, setError }, ref) => {
  const qrRef = useRef(null);
  const qrContainerRef = useRef(null);
  const cardUrl = `${FRONTEND_BASE_URL}/card/${cardId}`;

  useEffect(() => {
    const setupQRCode = async () => {
      if (!emailSignatureState[cardId]?.emailSignatureIncludeQRCode) {
        setLoading(false);
        return;
      }

      try {
        await initializeQRCode({
          qrContainerRef,
          qrRef,
          cardUrl,
          emailSignatureState,
          cardId
        });
        setLoading(false);
      } catch (error) {
        console.error('QR setup error:', error);
        setError('Failed to generate QR code');
        setLoading(false);
      }
    };

    setupQRCode();
  }, [cardUrl, emailSignatureState, cardId, setLoading, setError]);

  const hasProfilePicture = !!emailSignatureState[cardId]?.emailSignatureProfilePictureUrl;
  const hasCompanyLogo = !!emailSignatureState[cardId]?.emailSignatureCompanyLogoUrl;
  const profilePictureUrl = emailSignatureState[cardId]?.emailSignatureProfilePictureUrl || avatarPlaceholder;

  const renderProfileSection = () => (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
    }}>
      {/* Profile picture - always show, using placeholder if no profile picture */}
      <Box sx={{ position: 'relative', display: 'inline-block', marginBottom: 2 }}>
        <Avatar
          src={profilePictureUrl}
          sx={{
            width: '90px',
            height: '90px',
            backgroundColor: '#ffffff',
            border: '2px solid #e0e0e0',
          }}
        />
        {/* Show company logo as overlay when company logo exists */}
        {hasCompanyLogo && (
          <Avatar
            src={emailSignatureState[cardId]?.emailSignatureCompanyLogoUrl}
            sx={{
              width: '40px',
              height: '40px',
              backgroundColor: '#ffffff',
              border: '2px solid #e0e0e0',
              position: 'absolute',
              bottom: 0,
              right: 0,
              transform: 'translate(20%, 20%)',
            }}
          />
        )}
      </Box>

      {/* Text content */}
      <Box sx={{ mt: 2 }}>
        {emailSignatureState[cardId]?.emailSignatureName && (
          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
            {emailSignatureState[cardId].emailSignatureName}
          </Typography>
        )}

        {emailSignatureState[cardId]?.emailSignatureJobTitle && (
          <Typography variant="body1" sx={{ fontSize: '0.7rem', color: '#4f4f4f' }}>
            {emailSignatureState[cardId].emailSignatureJobTitle}
          </Typography>
        )}

        {emailSignatureState[cardId]?.emailSignatureCompany && (
          <Typography variant="body1" sx={{ fontSize: '0.7rem', color: '#4f4f4f' }}>
            {emailSignatureState[cardId].emailSignatureCompany}
          </Typography>
        )}

        {emailSignatureState[cardId]?.emailSignatureLocation && (
          <Typography variant="body1" sx={{
            fontSize: '0.7rem',
            marginTop: '10px',
            color: '#828282'
          }}>
            {emailSignatureState[cardId].emailSignatureLocation}
          </Typography>
        )}

        {emailSignatureState[cardId]?.emailSignaturePhoneNumber && (
          <Typography variant="body1" sx={{ fontSize: '0.7rem', color: '#828282' }}>
            {emailSignatureState[cardId].emailSignaturePhoneNumber}
          </Typography>
        )}
      </Box>
    </Box>
  );

  const renderQRSection = () => (
    emailSignatureState[cardId]?.emailSignatureIncludeQRCode && (
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        gap: 1
      }}>
        <Typography variant="body1" sx={{ fontSize: '0.7rem', fontWeight: '500', color: '#828282' }}>
          Connect with me
        </Typography>

        <Box
          id="qrContainerRef"
          ref={qrContainerRef}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: 'auto',
            marginTop: '20px',
            '& canvas, & svg': {
              maxWidth: '140px',
              height: 'auto',
            }
          }}
        />

        <MuiLink
          href={`/card/${cardId}`}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            color: '#2aaff7',
            textDecoration: 'none',
            marginBottom: 2,
            fontSize: '0.8rem',
            fontWeight: '500',
          }}
        >
          My Digital Business Card
        </MuiLink>
      </Box>
    )
  );

  return (
    <Box ref={ref}>
      <Box
        display="flex"
        flexDirection="column"
        sx={{
          padding: 2,
          backgroundColor: 'white',
          maxWidth: '600px',
          margin: 'auto',
          position: 'relative',
        }}
      >
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start' // Align items at the top to ensure proper alignment when no profile picture
        }}>
          {renderProfileSection()}
          {renderQRSection()}
        </Box>

        <Box sx={{ mt: 1 }}>
          {emailSignatureState[cardId]?.emailSignatureIncludeBannerImage &&
            emailSignatureState[cardId]?.emailSignatureBannerImageUrl && (
              <Box sx={{ mb: 1 }}>
                <img
                  src={emailSignatureState[cardId]?.emailSignatureBannerImageUrl}
                  alt="Banner"
                  style={{ width: '100%', height: 'auto' }}
                />
              </Box>
            )}

          {emailSignatureState[cardId]?.emailSignatureDisclaimer && (
            <Typography variant="body1" sx={{ fontSize: '0.7rem', marginTop: '10px', color: '#bdbdbd', fontStyle: 'italic' }}>
              {emailSignatureState[cardId].emailSignatureDisclaimer}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
});

export default SignatureContent;